import {
  type SortKey,
  CircleLoader,
  Descriptions,
  SecondaryButton,
  Space,
  StatusBadge,
  Text,
} from '@procurify/ui'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { type PaginationID } from '@webapp/core/providers/Pagination/pagination.types'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  type ReportExportTask,
  type ReportExportTaskFilter,
} from '@webapp/modules/reports/entities/ReportExportTask'
import messages from './messages'

export interface IExportTaskHistoryTableProps {
  items: ReportExportTask[]
  isLoading: boolean
  sortOptions: SortKey[]
  /** Delete confirmation modal + command live at the FS (ADR-9, BHV-037) */
  onDeleteRequest: (task: ReportExportTask) => void
  paginationId: PaginationID
}

// Formats a YYYY-MM-DD date at local midnight (a bare `new Date(date)`
// parses as UTC and shifts the displayed day west of Greenwich).
const parseLocalDate = (date: string) => new Date(`${date}T00:00:00`)

export const ExportTaskHistoryTable = ({
  items,
  isLoading,
  sortOptions,
  onDeleteRequest,
  paginationId,
}: IExportTaskHistoryTableProps) => {
  const intl = useProcurifyIntl()

  const formatDate = (date: Date) =>
    intl.formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })

  // Date-aware Filter Options rendering ({ type: 'date' } entries carry a
  // YYYY-MM-DD value — BHV-036).
  const formatFilterValue = (filter: ReportExportTaskFilter) =>
    filter.type === 'date'
      ? formatDate(parseLocalDate(filter.value))
      : filter.value

  const renderStatusCell = (task: ReportExportTask) => {
    if (task.isPreparing) {
      return (
        <Space direction='horizontal' size='small' align='center'>
          <CircleLoader size={16} />
          <Text autoMargin={false}>
            {intl.formatMessage(messages.statusPreparingText)}
          </Text>
        </Space>
      )
    }

    if (task.isFailed) {
      return (
        <StatusBadge
          type='error'
          text={intl.formatMessage(messages.statusFailedText)}
        />
      )
    }

    return (
      <SecondaryButton
        type='button'
        icon='download'
        onClick={() => {
          if (task.file) {
            // Backend attachment URL (file download), not an SPA route
            // (BHV-036).
            window.location.assign(`/api/v3/attachments/${task.file.uuid}`)
          }
        }}
      >
        {intl.formatMessage(messages.downloadButtonText)}
      </SecondaryButton>
    )
  }

  const columns = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnDatePrepared),
      maxWidth: 180,
      getter: (task: ReportExportTask) => formatDate(task.createdAt),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnFilterOptions),
      getter: (task: ReportExportTask) =>
        task.hasFilters ? (
          <Descriptions columns={1}>
            {task.filters.map((filter) => (
              <Descriptions.Item key={filter.label} label={filter.label}>
                {formatFilterValue(filter)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        ) : (
          intl.formatMessage(messages.noFiltersText)
        ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnStatus),
      maxWidth: 180,
      getter: renderStatusCell,
    }),
  ]

  return (
    <PaginationControls id={paginationId}>
      <Space size='large'>
        <Space direction='horizontal'>
          <Space.Item minWidth={240}>
            <PaginationControls.Sort sortKeys={sortOptions} />
          </Space.Item>
        </Space>

        <DataTable<ReportExportTask>
          id='export-task-history-table'
          aria-label={intl.formatMessage(messages.tableAriaLabel)}
          data={items}
          isLoading={isLoading}
          columns={columns}
          customizableColumns={false}
          persistCustomizedColumns={false}
          emptyStateText={intl.formatMessage(messages.emptyStateText)}
          emptyStateSize='small'
          menuListItems={(task) => [
            {
              icon: 'delete',
              text: intl.formatMessage(messages.deleteRecordMenuItemText),
              onClick: () => onDeleteRequest(task),
            },
          ]}
        />

        <Space direction='horizontal' justify='space-between'>
          <PaginationControls.PageLimit />
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

ExportTaskHistoryTable.displayName = 'ExportTaskHistoryTable'
